// "Looks" - one-click looks at the top of Look and feel. Each tile is a tiny start page drawn
// in the look's own colors, font and backdrop (shared/look-preview.js), and applies theme +
// accent + font + density + corners + wallpaper at once. Users can save the current look,
// shuffle a random one, and share/import looks as files. Tweaking the individual controls
// still works afterward.
import * as quests from "../../app/quests.js";
import { metric } from "../../shared/metric.js";
import { button } from "../ui.js";
import { subscribe } from "../../shared/settings-store.js";
import { BUNDLES, LOOK_GROUPS, WALLPAPER_PRESET_MAP } from "../../shared/settings-schema.js";
import { lookScene } from "../../shared/look-preview.js";
import { FONTS, loadFont, loadDisplayFont } from "../../shared/fonts.js";
import { applyBundle, getCustomBundles, saveCustomBundle, deleteCustomBundle, subscribeBundles, bundleMatches, exportLook, applyLook, randomLook } from "../../shared/bundles.js";
import { downloadJSON, pickJSONFile } from "../../shared/backup.js";
import { getAll, setMany } from "../../shared/settings-store.js";
import { cachedPerks, SUPPORTER_KEY } from "../../shared/supporter.js";
import { lookAccess, noteLocked, noteFree, TRY_ON_MS, LOOK_REVERTED_KEY } from "../../shared/look-perks.js";
import { lookListed } from "../picker-lists.js";

const X = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><line x1="4" y1="4" x2="10" y2="10"/><line x1="10" y1="4" x2="4" y2="10"/></svg>';
const CHECK = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 8.5l3 3 6-7"/></svg>';
const FONT_API = { FONTS, loadFont, loadDisplayFont };
const PHONE_ROW = 4; // tiles a collapsed group shows on a phone
const DICE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8" cy="8" r="1.2" fill="currentColor"/><circle cx="16" cy="8" r="1.2" fill="currentColor"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/><circle cx="8" cy="16" r="1.2" fill="currentColor"/><circle cx="16" cy="16" r="1.2" fill="currentColor"/></svg>';

export function looksControl() {
	const gallery = document.createElement("div");
	gallery.className = "rv-looks";
	gallery.id = "rv-looks";

	// A tile is an apply <button> plus (for custom looks) a sibling delete <button>, wrapped
	// in a positioned container - never nested interactive content.
	// The 60-second try-on of a Supporter look: the student's own look comes back by itself.
	const tryBar = document.createElement("div");
	tryBar.className = "rv-look-try";
	tryBar.setAttribute("role", "status");
	let trying = null;
	function endTry(say) {
		if (!trying) return;
		clearTimeout(trying.timer);
		setMany(trying.prev);
		noteFree(trying.prev);
		trying = null;
		tryBar.textContent = say ? "Your own look is back." : "";
	}
	function startTry(b) {
		const all = getAll();
		const prev = trying ? trying.prev : Object.fromEntries(Object.keys(b.settings).map((k) => [k, all[k]]));
		if (trying) clearTimeout(trying.timer);
		applyBundle(b.settings);
		noteLocked(b);
		trying = { prev, timer: setTimeout(() => endTry(true), TRY_ON_MS) };
		tryBar.textContent = "";
		const msg = document.createElement("span");
		msg.textContent = "Trying on " + b.label + ". Your own look comes back by itself in a minute. Supporter looks come with a pass.";
		tryBar.append(msg, button("Put my look back now", () => endTry(true)), button("About passes", () => { if (window.top !== window) window.top.postMessage({ type: "revault-open", url: "/supporter" }, location.origin); else location.href = "/supporter"; }));
	}

	function tile(b, custom) {
		const access = custom ? "free" : lookAccess(b, cachedPerks());
		const wrap = document.createElement("div");
		wrap.className = "rv-bundle-wrap";
		const t = document.createElement("button");
		t.type = "button";
		t.className = "rv-bundle";
		t._settings = b.settings; // bound look, so active-sync doesn't rely on tile order
		t.setAttribute("aria-label", b.label);
		const cap = document.createElement("span");
		cap.className = "rv-bundle-cap";
		const name = document.createElement("span");
		name.className = "rv-bundle-name";
		name.textContent = b.label;
		const accent = document.createElement("span");
		accent.className = "rv-bundle-accent";
		accent.style.background = b.settings.accent || "#888";
		cap.append(name, accent);
		if (access !== "free") {
			const tag = document.createElement("span");
			tag.className = "rv-bundle-tag";
			tag.textContent = access === "early" ? "Early" : "Supporter look";
			cap.append(tag);
			t.setAttribute("aria-label", b.label + ", " + tag.textContent + (access === "try" ? ", try it on for a minute" : ""));
		}
		const check = document.createElement("span");
		check.className = "rv-bundle-check";
		check.innerHTML = CHECK;
		t.append(lookScene(b, WALLPAPER_PRESET_MAP, FONT_API), cap, check);
		setActive(t);
		t.addEventListener("click", () => {
			if (access === "try") { startTry(b); metric("look:try"); return; }
			if (trying) { clearTimeout(trying.timer); trying = null; tryBar.textContent = ""; }
			applyBundle(b.settings);
			if (access === "free") noteFree(b.settings); else noteLocked(b);
			quests.track("customize"); metric("look:" + (custom ? "custom" : b.id));
		});
		wrap.append(t);
		if (custom) {
			const del = document.createElement("button");
			del.type = "button";
			del.className = "rv-bundle-del";
			del.setAttribute("aria-label", "Delete look " + b.label);
			del.title = "Delete";
			del.innerHTML = X;
			del.addEventListener("click", () => { if (confirm("Delete the “" + b.label + "” look?")) deleteCustomBundle(b.id); });
			wrap.append(del);
		}
		return wrap;
	}
	function setActive(t) {
		const on = !!t._settings && bundleMatches(t._settings);
		t.classList.toggle("active", on);
		t.setAttribute("aria-pressed", String(on));
	}

	// The "Shuffle" tile applies a random built-in look. It's intentionally NOT a
	// .rv-bundle, so the active-sync below skips it.
	function shuffleTile() {
		const wrap = document.createElement("div");
		wrap.className = "rv-bundle-wrap";
		const t = document.createElement("button");
		t.type = "button";
		t.className = "rv-bundle-shuffle";
		t.setAttribute("aria-label", "Apply a random look");
		t.innerHTML = DICE + "<span>Shuffle</span>";
		t.addEventListener("click", () => { applyBundle(randomLook()); metric("look:random"); });
		wrap.append(t);
		return wrap;
	}

	const open = new Set(); // groups expanded on a phone, kept across re-renders
	function section(title, items, collapsible, season) {
		const sec = document.createElement("section");
		sec.className = "rv-looks-group";
		if (season) sec.dataset.season = "1"; // stays in view when the grid is folded
		const h = document.createElement("h3");
		h.className = "rv-looks-h";
		h.textContent = title;
		const grid = document.createElement("div");
		grid.className = "rv-bundles";
		items.forEach((x) => grid.append(x));
		sec.append(h, grid);
		const tiles = items.length;
		if (collapsible && tiles > PHONE_ROW) {
			sec.classList.add("rv-looks-collapsible");
			if (open.has(title)) sec.classList.add("open");
			const more = document.createElement("button");
			more.type = "button";
			more.className = "rv-looks-more";
			grid.id = "rv-looks-" + title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
			more.setAttribute("aria-controls", grid.id);
			const sync = () => {
				const isOpen = sec.classList.contains("open");
				more.textContent = isOpen ? "Show fewer" : "Show all (" + tiles + ")";
				more.setAttribute("aria-expanded", String(isOpen));
			};
			more.addEventListener("click", () => {
				sec.classList.toggle("open");
				if (sec.classList.contains("open")) open.add(title); else open.delete(title);
				sync();
			});
			sync();
			sec.append(more);
		}
		return sec;
	}

	function render() {
		gallery.textContent = "";
		const perks = cachedPerks();
		const byId = new Map(BUNDLES.filter((b) => lookListed(b, perks)).map((b) => [b.id, b]));
		const placed = new Set();
		LOOK_GROUPS.forEach(([title, ids], i) => {
			const list = ids.map((id) => byId.get(id)).filter(Boolean);
			if (!list.length) return;
			list.forEach((b) => placed.add(b.id));
			const tiles = list.map((b) => tile(b, false));
			if (i === 0) tiles.push(shuffleTile());
			gallery.append(section(title, tiles, i > 0, list.some((b) => b.season)));
		});
		const rest = [...byId.values()].filter((b) => !placed.has(b.id)).map((b) => tile(b, false));
		if (rest.length) gallery.append(section("More", rest, true));
		const mine = getCustomBundles().map((b) => tile(b, true));
		if (mine.length) gallery.append(section("Yours", mine, false));
	}
	render();

	// Cheap active-state refresh on settings changes (reads each tile's bound look);
	// full rebuild only when the saved list changes (added/removed in any tab).
	function syncActive() {
		gallery.querySelectorAll(".rv-bundle").forEach(setActive);
	}
	subscribe(syncActive);
	subscribeBundles(render);
	window.addEventListener("storage", (e) => { if (e.key === SUPPORTER_KEY) render(); });

	const save = button("Save this look…", () => {
		const name = prompt("Name this look:");
		if (name && name.trim()) saveCustomBundle(name);
	});
	const exp = button("Export", () => downloadJSON("revault-look.json", exportLook()));
	const imp = button("Import", () => pickJSONFile((obj, err) => {
		if (err || !applyLook(obj)) alert("That isn’t a valid Re:Vault look file.");
	}));
	const actions = document.createElement("div");
	actions.className = "rv-btnrow";
	actions.append(save, exp, imp);

	const wrap = document.createElement("div");
	wrap.className = "rv-looks-wrap";
	const shown = () => BUNDLES.filter((b) => lookListed(b, cachedPerks())).length + getCustomBundles().length;
	wrap.append(actions, tryBar, foldable(gallery, shown, "looks"));
	// A Supporter or early look went back to the last free look on this load: say so once, quietly.
	try {
		const rv = JSON.parse(localStorage.getItem(LOOK_REVERTED_KEY) || "null");
		if (rv) {
			const b = BUNDLES.find((x) => x.id === rv.id);
			tryBar.textContent = (b ? b.label : "That look") + " is a Supporter look, so your last look is back.";
			localStorage.removeItem(LOOK_REVERTED_KEY);
		}
	} catch (e) { void e; }
	return wrap;
}

// Show one row of a gallery and a "See all" button that opens the rest in place. The row is
// whatever fits the width, worked out from the grid's columns, and the chosen tile always shows.
export function foldable(gallery, count, noun) {
	const box = document.createElement("div");
	box.className = "rv-fold";
	const more = document.createElement("button");
	more.type = "button";
	more.className = "rv-btn rv-fold-btn";
	more.setAttribute("aria-controls", gallery.id || (gallery.id = "rv-fold-" + noun));
	let open = false;
	const apply = () => {
		box.classList.toggle("open", open);
		more.setAttribute("aria-expanded", String(open));
		more.textContent = open ? "Show fewer" : "See all " + count() + " " + noun;
		const groups = [...gallery.querySelectorAll(".rv-looks-group")];
		const grids = groups.length ? groups.map((g) => g.querySelector(".rv-bundles")) : [gallery];
		// A season's looks stay in view below the first row while the season is on, so they're easy
		// to find without being applied for anyone.
		groups.forEach((g, i) => { g.hidden = !open && i > 0 && !g.dataset.season; });
		const grid = grids[0];
		const items = [...grid.children];
		for (const g of groups) if (open || g.dataset.season) for (const t of g.querySelector(".rv-bundles").children) t.hidden = false;
		if (open) { if (!groups.length) for (const t of gallery.children) t.hidden = false; return; }
		const cols = Math.max(1, getComputedStyle(grid).gridTemplateColumns.split(" ").filter(Boolean).length);
		const on = gallery.querySelector(".active, [aria-pressed=\"true\"]");
		const onItem = on && items.find((t) => t.contains(on));
		const late = onItem && items.indexOf(onItem) >= cols;
		const keep = new Set(items.slice(0, late ? cols - 1 : cols));
		if (onItem) keep.add(onItem);
		items.forEach((t) => { t.hidden = !keep.has(t); });
		// a look chosen from a folded group still shows in the row
		if (on && !onItem) { const w = on.closest(".rv-bundle-wrap") || on; const g = w.closest(".rv-looks-group"); if (g && !g.dataset.season) { g.hidden = false; for (const t of g.querySelector(".rv-bundles").children) t.hidden = t !== w; } }
	};
	more.addEventListener("click", () => { open = !open; apply(); if (!open) more.scrollIntoView({ block: "nearest" }); });
	box.append(gallery, more);
	box.fold = { apply, open: () => { if (!open) { open = true; apply(); } } };
	if (typeof ResizeObserver !== "undefined") new ResizeObserver(() => { if (!open) apply(); }).observe(gallery);
	new MutationObserver(() => { if (!open) requestAnimationFrame(apply); }).observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-pressed"] });
	requestAnimationFrame(apply);
	return box;
}
